import { Alert, Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { pluralize } from "@entrilia/shared/utilities/stringHelper";
import biApi from "../../../../../../../../../api/biApi";
import type { MeasureSubgroup } from "../../../../../../../../../api/types/svTypes.ts";
import { selectAllMeasures } from "../../../../../store/selectors/metaDataSelectors";
import { createMetaDataAction } from "../../../../../store/utils/storeUtils";
import { countMeasuresInSubgroup } from "../utilities/measureSubgroups";

const deleteMeasureSubgroup = withErrorHandling(biApi.deleteMeasureSubgroup);

interface Props {
  subgroup: MeasureSubgroup;
  onClose: () => void;
}

const DeleteSubgroupDialog = ({ subgroup, onClose }: Props) => {
  const dispatch = useDispatch();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const allMeasures = useSelector(selectAllMeasures);

  const [deleting, setDeleting] = useState(false);

  const measureCount = countMeasuresInSubgroup(allMeasures, subgroup);

  const handleDelete = async () => {
    setDeleting(true);
    const [, error] = await deleteMeasureSubgroup(subgroup.id);
    setDeleting(false);

    if (error) {
      logError(error, "[DeleteSubgroupDialog] deleteMeasureSubgroup");
      sendNotificationError(error.message);
      if (error.type === "NotFound") {
        dispatch(createMetaDataAction("deleteMeasureSubgroup", subgroup.id));
        onClose();
      }
      return;
    }

    dispatch(createMetaDataAction("deleteMeasureSubgroup", subgroup.id));
    sendNotification("Subgroup deleted successfully");
    onClose();
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Delete</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={3}>
          <Stack spacing={2} sx={{ alignItems: "center" }}>
            <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
            <Stack spacing={1} sx={{ alignItems: "center" }}>
              <Typography variant="h6" sx={{ textAlign: "center", wordBreak: "break-word" }}>
                Delete {subgroup.name}?
              </Typography>
              <Typography color="textSecondary" sx={{ textAlign: "center" }}>
                You’re about to delete this subgroup for measures. This action is permanent and can’t be undone.
              </Typography>
            </Stack>
          </Stack>
          {measureCount > 0 && (
            <Alert severity="warning">
              <strong>
                {measureCount} {pluralize("measure", measureCount)}
              </strong>{" "}
              in this subgroup will be moved to the parent group.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose} cancelDisabled={deleting}>
        <Button variant="outlined" color="error" loading={deleting} onClick={handleDelete}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteSubgroupDialog;
